// ============================================
// AGREGARALISTAMODAL.JS — Modal para agregar item a lista pactada
// Acepta `item` con opciones múltiples (p.ej. 4 niveles de un cable) o
// una sola opción directa.
// ============================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';
import { margenDePrecioCosto, CampoPrecio, round2 } from './LineaPrecioEditable';
import { fmtNumDinero } from '../utils/dinero';

// SPRINT DINERO 2: margen CANONICO sobre venta = (precio - costo) / precio * 100.
// "Margen 25%" significa que el 25% del precio de venta es ganancia, por lo que
// el precio sugerido es costo / (1 - 0.25) — NUNCA costo * 1.25 (eso es solo 20%
// de margen real y era la formula vieja inflada).
const MARGEN_SUGERIDO_PCT = 25;
// SIN-REDONDEO (2026-10-12): antes se redondeaba a PESOS ENTEROS con Math.round y ese entero se
// precargaba como precio pactado y se guardaba. Ahora se queda en centavos (round2).
const precioSugerido = (costo) => round2(costo / (1 - MARGEN_SUGERIDO_PCT / 100));

// item: {
//   nombre_display: string,
//   // Opción A: item único
//   tipo?, referencia_id?, precio_base_mxn?
//   // Opción B: varias opciones (p.ej. 4 niveles de un cable)
//   opciones?: [{ label, tipo, referencia_id, precio_base_mxn }]
// }
export default function AgregarAListaModal({ item, onCerrar, onGuardado }) {
  const tieneOpciones = Array.isArray(item?.opciones) && item.opciones.length > 0;
  const [listas, setListas] = useState([]);
  const [listaId, setListaId] = useState('');
  const [opcionIdx, setOpcionIdx] = useState(0);
  const opcionSeleccionada = tieneOpciones ? item.opciones[opcionIdx] : item;

  const [precio, setPrecio] = useState(
    opcionSeleccionada?.precio_base_mxn != null ? String(opcionSeleccionada.precio_base_mxn) : ''
  );
  const [notas, setNotas] = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_CLI}/api/listas`, { headers: authHeaders() }).then(x => x.json());
        if (r.ok) {
          const activas = (r.listas || []).filter(l => l.activa);
          setListas(activas);
          if (activas.length > 0) setListaId(String(activas[0].id));
        }
      } finally { setCargando(false); }
    })();
  }, []);

  // Al cambiar la opción, resetear el precio a la sugerencia por defecto
  // (margen canonico del 25% sobre venta: costo / 0.75).
  useEffect(() => {
    const base = Number(opcionSeleccionada?.precio_base_mxn) || 0;
    if (base > 0) setPrecio(String(precioSugerido(base)));
    else setPrecio('');
    // eslint-disable-next-line
  }, [opcionIdx]);

  const costo = Number(opcionSeleccionada?.precio_base_mxn) || 0;
  // SPRINT DINERO 2: helper canonico compartido (sobre venta), no formula local.
  const margenEstimado = useMemo(() => {
    const p = Number(precio);
    if (!Number.isFinite(p) || costo <= 0) return null;
    return margenDePrecioCosto(p, costo);
  }, [precio, costo]);

  const margenColor = (pct) => {
    if (pct == null) return COLOR_GRIS_CLARO;
    if (pct >= 25) return '#16a34a';
    if (pct >= 15) return '#f59e0b';
    return '#e05c5c';
  };
  const margenIcono = (pct) => {
    if (pct == null) return '';
    if (pct >= 25) return '🟢';
    if (pct >= 15) return '🟡';
    return '🔴';
  };

  const guardar = async () => {
    if (!listaId) { setError('Selecciona una lista'); return; }
    if (!opcionSeleccionada?.tipo || !opcionSeleccionada?.referencia_id) {
      setError('Selecciona una opción'); return;
    }
    const p = Number(precio);
    if (String(precio).trim() === '' || !Number.isFinite(p) || p < 0) { setError('Precio pactado inválido'); return; }
    setError(null);
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/listas/${listaId}/items`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: opcionSeleccionada.tipo,
          referencia_id: opcionSeleccionada.referencia_id,
          precio_pactado_mxn: p,
          notas: notas || null,
        }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      onGuardado && onGuardado(r.item);
    } catch (e) {
      setError(e.message);
    } finally { setGuardando(false); }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1250,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
          fontFamily: PP, color: COLOR_GRIS, padding: 24, maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 4 }}>
          Agregar a lista pactada
        </div>
        <div style={{ fontSize: 13, color: COLOR_GRIS, marginBottom: 16 }}>
          {item?.nombre_display}
        </div>

        {cargando ? (
          <div style={{ color: COLOR_GRIS_CLARO }}>Cargando listas…</div>
        ) : listas.length === 0 ? (
          <div style={{
            padding: 16, background: COLOR_FONDO, borderRadius: 8,
            fontSize: 13, color: COLOR_GRIS,
          }}>
            No hay listas activas. Crea una desde la sección <strong>Listas pactadas</strong> primero.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Lista</div>
              <select style={estiloInput} value={listaId} onChange={(e) => setListaId(e.target.value)}>
                {listas.map(l => <option key={l.id} value={l.id}>📋 {l.nombre}</option>)}
              </select>
            </div>

            {tieneOpciones && (
              <div style={{ marginBottom: 12 }}>
                <div style={lbl}>Opción / nivel</div>
                <select
                  style={estiloInput}
                  value={opcionIdx}
                  onChange={(e) => setOpcionIdx(Number(e.target.value))}
                >
                  {item.opciones.map((o, i) => (
                    <option key={i} value={i}>
                      {o.label} — costo ${fmtNumDinero(o.precio_base_mxn)} MXN
                    </option>
                  ))}
                </select>
              </div>
            )}

            {costo > 0 && (
              <div style={{
                padding: 10, background: COLOR_FONDO, borderRadius: 8, marginBottom: 12,
                fontSize: 12, display: 'grid', gap: 4,
              }}>
                <div>Costo Beep: <strong>${fmtNumDinero(costo)}</strong> MXN</div>
                <div>Sugerido (margen 25%): <strong>${fmtNumDinero(precioSugerido(costo))}</strong> MXN</div>
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Precio pactado MXN *</div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: 9, color: COLOR_GRIS_CLARO, fontSize: 13 }}>$</span>
                <CampoPrecio style={{ ...estiloInput, paddingLeft: 22 }}
                  value={precio}
                  onChange={(e) => setPrecio(e.target.value)} />
              </div>
              {margenEstimado != null && (
                <div style={{ fontSize: 11, color: margenColor(margenEstimado), marginTop: 4, fontWeight: 600 }}>
                  Margen estimado: {margenEstimado.toFixed(1)}% {margenIcono(margenEstimado)}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={lbl}>Notas (opcional)</div>
              <textarea style={{ ...estiloInput, minHeight: 50, resize: 'vertical' }}
                value={notas} onChange={(e) => setNotas(e.target.value)} />
            </div>

            {error && (
              <div style={{ marginBottom: 12, padding: 10, background: '#fde8e8', borderRadius: 8, fontSize: 12, color: '#8e3838' }}>
                ⚠️ {error}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar} disabled={guardando}>Cancelar</button>
          {listas.length > 0 && (
            <button style={{ ...estiloBotonPrimario, opacity: guardando ? 0.6 : 1 }}
              onClick={guardar} disabled={guardando}>
              {guardando ? 'Agregando…' : 'Agregar a lista'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const lbl = { fontSize: 11, fontWeight: 600, color: COLOR_GRIS, marginBottom: 4 };
